import { Languages, LogOut, Sparkles, Shield, User as UserIcon, ChevronDown, Zap, AlertTriangle, Coins, LifeBuoy, BookOpen, MessageSquare, Lightbulb, Ticket, Activity } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

// Formats a raw minute count into something a human reads at a glance.
// 0 → "0m", 42 → "42m", 60 → "1h", 214 → "3h 34m", huge → "Unlimited".
// The huge-number branch catches the Admin plan (10 million minutes) so
// the dropdown doesn't show a nonsense "166663h 11m left" chip.
const UNLIMITED_THRESHOLD_MIN = 100_000; // ~1666 hours; well above any real plan (Studio = 900m)
const fmtMinutes = (m) => {
  const mins = Math.max(0, Math.round(m ?? 0));
  if (mins >= UNLIMITED_THRESHOLD_MIN) return "Unlimited";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
};

// Compact form used in the collapsed nav pill — the dropdown gets more
// real estate so it can render "Unlimited" without truncation.
const fmtMinutesPill = (m) => {
  const mins = Math.max(0, Math.round(m ?? 0));
  if (mins >= UNLIMITED_THRESHOLD_MIN) return "\u221E"; // infinity glyph
  return fmtMinutes(mins);
};

export default function AppNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  // Guard against firing the same toast twice on a single tab (React
  // StrictMode + navigation-remounts would otherwise trigger the effect
  // more than once per session).
  const nudgeFiredRef = useRef({ low: false, empty: false });

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/usage");
      setUsage(data);
    } catch (err) {
      // Non-fatal — the profile button still works without usage numbers,
      // it just falls back to name/email only.
      if (process.env.NODE_ENV !== "production") console.warn("Usage fetch failed:", err?.message);
    }
  }, []);

  useEffect(() => {
    if (user) loadUsage();
  }, [user, loadUsage]);

  // Session-scoped billing nudges. Two states we care about:
  //   1. Under 5 minutes left → link to current paid-pack pricing
  //   2. Exactly zero left     → firmer, sticky "top up to keep going" toast
  // Both fire at most ONCE per browser tab (sessionStorage) so users don't
  // get spammed if they navigate around the app. We skip both for admins
  // and anyone already on a paid plan — they don't need the nudge.
  useEffect(() => {
    if (!usage || !user) return;
    if (usage.is_admin || usage.is_paid) return;

    const remaining = usage.minutes_remaining ?? 0;

    // Zero / negative → firm prompt to top up. Sticky (no auto-dismiss)
    // because the user literally can't transcribe anything until they buy.
    if (remaining <= 0) {
      const key = "linguasub_nudge_empty_shown";
      if (!nudgeFiredRef.current.empty && !sessionStorage.getItem(key)) {
        nudgeFiredRef.current.empty = true;
        sessionStorage.setItem(key, "1");
        toast(
          "Your one-time free balance has been used up.",
          {
            description: "Top up with any pack to keep transcribing — no auto-charge, you pick when.",
            duration: Infinity,
            position: "bottom-right",
            action: {
              label: "See packs",
              onClick: () => navigate("/pricing"),
            },
            icon: <AlertTriangle className="w-4 h-4 text-red-400" />,
            className: "linguasub-nudge-empty",
          }
        );
      }
      return;
    }

    // Under 5 min left → quiet 8-second toast; only fires once per tab.
    if (remaining < 5) {
      const key = "linguasub_nudge_low_shown";
      if (!nudgeFiredRef.current.low && !sessionStorage.getItem(key)) {
        nudgeFiredRef.current.low = true;
        sessionStorage.setItem(key, "1");
        toast(
          `${Math.floor(remaining)} minutes left — view ${usage.upgrade_offer?.name || "paid"} packs at current pricing`,
          {
            duration: 8000,
            position: "bottom-right",
            action: {
              label: "Upgrade",
              onClick: () => navigate("/pricing"),
            },
            className: "linguasub-nudge-low",
          }
        );
      }
    }
  }, [usage, user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const planKey = usage?.plan?.key || "free";
  const planName = usage?.plan?.name || "Your plan";
  const remaining = usage?.minutes_remaining ?? null;
  const quota = usage?.minutes_quota ?? 0;
  const used = usage?.minutes_used ?? 0;
  // Admin / other unbounded plans should never show a progress bar, red
  // warnings, or a "3h 29m used this cycle" chip — those numbers are
  // meaningless when the quota is effectively infinite.
  const isUnbounded = planKey === "admin" || quota >= UNLIMITED_THRESHOLD_MIN;
  const pctUsed = !isUnbounded && quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  // Colour the remaining-minutes chip red if the user is close to running
  // out. Only applies to bounded plans.
  const isLow = !isUnbounded && remaining !== null && quota > 0 && (remaining <= 5 || remaining / quota < 0.2);

  return (
    <nav className="fixed top-0 inset-x-0 z-50 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="nav-home-link">
          <Languages className="w-5 h-5 text-blue-500" />
          <span className="font-heading font-black tracking-tighter text-lg">LinguaSub</span>
          <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-zinc-500 ml-3 border-l border-zinc-800 pl-3">
            Subtitle Studio
          </span>
        </Link>
        <div className="flex items-center gap-3">
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-emerald-300 hover:text-white px-3 py-1.5 border border-emerald-500/30 hover:bg-emerald-500/10 transition-colors duration-200"
            >
              <Shield className="w-3 h-3" /> Admin
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-support-trigger"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white px-2.5 sm:px-3 py-1.5 border border-zinc-800 hover:bg-zinc-800 transition-colors duration-200"
              aria-label="Open help and support menu"
            >
              <LifeBuoy className="w-3 h-3" />
              <span className="hidden sm:inline">Help &amp; Support</span>
              <ChevronDown className="w-3 h-3 opacity-60" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              data-testid="nav-support-menu"
              className="w-60 bg-zinc-950 border-zinc-800 text-white p-0 rounded-md overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/40 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Help &amp; Support
              </div>
              <div className="py-1.5">
                {[
                  { to: "/support?tab=help", label: "Help Center", icon: BookOpen, testId: "nav-support-help" },
                  { to: "/support?tab=report", label: "Report a Problem", icon: AlertTriangle, testId: "nav-support-report" },
                  { to: "/support?tab=feedback", label: "Send Feedback", icon: MessageSquare, testId: "nav-support-feedback" },
                  { to: "/support?tab=feature", label: "Feature Request", icon: Lightbulb, testId: "nav-support-feature" },
                  { to: "/support?tab=tickets", label: "My Tickets", icon: Ticket, testId: "nav-support-tickets" },
                ].map((item) => (
                  <DropdownMenuItem asChild key={item.to}>
                    <Link
                      to={item.to}
                      data-testid={item.testId}
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer focus:bg-zinc-800 focus:text-white"
                    >
                      <item.icon className="w-3.5 h-3.5 text-zinc-500" />
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/pricing"
            data-testid="nav-pricing-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-1.5 border border-zinc-800 hover:bg-zinc-800 transition-colors duration-200"
          >
            <Sparkles className="w-3 h-3" /> Pricing
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="profile-menu-trigger"
              className="flex items-center gap-2.5 h-9 pl-2 pr-2 md:pr-3 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/60 transition-colors duration-150 rounded-md group"
              aria-label="Open profile menu"
            >
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || "avatar"}
                  data-testid="user-avatar"
                  className="w-6 h-6 rounded-full border border-zinc-800 shrink-0"
                />
              ) : (
                <span className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/40 grid place-items-center text-[10px] font-bold text-blue-300 shrink-0">
                  {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                </span>
              )}
              <span className="hidden md:flex flex-col items-start leading-tight max-w-[160px]">
                <span
                  className="text-xs text-white truncate w-full text-left"
                  data-testid="user-name"
                >
                  {user?.name || "Account"}
                </span>
                <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  <span data-testid="user-plan-pill">{planName}</span>
                  {remaining !== null && (
                    <>
                      <span className="text-zinc-700">·</span>
                      <span
                        data-testid="user-minutes-pill"
                        className={
                          isUnbounded ? "text-emerald-400"
                          : isLow ? "text-red-400"
                          : "text-emerald-400"
                        }
                      >
                        {isUnbounded ? "Unlimited" : `${fmtMinutesPill(remaining)} left`}
                      </span>
                    </>
                  )}
                </span>
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-zinc-500 group-hover:text-white transition-colors" />
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              sideOffset={8}
              data-testid="profile-menu-content"
              className="w-72 bg-zinc-950 border-zinc-800 text-white p-0 rounded-md overflow-hidden"
            >
              {/* Header card — name, email, avatar */}
              <div className="p-4 border-b border-zinc-800 bg-zinc-900/40">
                <div className="flex items-center gap-3">
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name || "avatar"}
                      className="w-10 h-10 rounded-full border border-zinc-800 shrink-0"
                    />
                  ) : (
                    <span className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-500/40 grid place-items-center text-sm font-bold text-blue-300 shrink-0">
                      {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-white truncate" data-testid="menu-user-name">
                      {user?.name || "Account"}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate" data-testid="menu-user-email">
                      {user?.email}
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan + usage bar */}
              <div className="p-4 border-b border-zinc-800">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    <Zap className="w-3 h-3" /> Current plan
                  </div>
                  <span
                    data-testid="menu-plan-name"
                    className={
                      "text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm " +
                      (planKey === "free"
                        ? "bg-zinc-800 text-zinc-300"
                        : "bg-blue-500/15 text-blue-300 border border-blue-500/30")
                    }
                  >
                    {planName}
                  </span>
                </div>
                {usage ? (
                  isUnbounded ? (
                    <div className="py-1" data-testid="menu-minutes-remaining">
                      <div className="font-heading text-2xl font-black tracking-tight text-emerald-400">
                        Unlimited
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 mt-1">
                        No monthly quota on this plan
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-between mb-1.5">
                        <span
                          className={
                            "font-heading text-2xl font-black tracking-tight " +
                            (isLow ? "text-red-400" : "text-white")
                          }
                          data-testid="menu-minutes-remaining"
                        >
                          {fmtMinutes(remaining)}
                        </span>
                        <span className="text-[10px] font-mono text-zinc-500">
                          of {fmtMinutes(quota)} left
                        </span>
                      </div>
                      <div
                        className="h-1.5 w-full bg-zinc-800 rounded-full overflow-hidden"
                        role="progressbar"
                        aria-valuenow={pctUsed}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                        <div
                          className={
                            "h-full transition-all duration-500 " +
                            (isLow ? "bg-red-500" : "bg-emerald-500")
                          }
                          style={{ width: `${pctUsed}%` }}
                        />
                      </div>
                      <div className="mt-1.5 text-[10px] font-mono text-zinc-600">
                        {fmtMinutes(used)} used · no automatic refill
                      </div>
                    </>
                  )
                ) : (
                  <div className="text-[11px] text-zinc-500">Loading usage…</div>
                )}
                {!isUnbounded && (
                  <Link
                    to="/pricing"
                    data-testid="menu-upgrade-link"
                    className="mt-3 block text-center w-full text-[11px] font-mono uppercase tracking-widest bg-blue-500 hover:bg-blue-400 text-white py-2 rounded-sm transition-colors"
                  >
                    {planKey === "free" || planKey === "starter"
                      ? "Upgrade plan \u2192"
                      : "Manage plan \u2192"}
                  </Link>
                )}
              </div>

              {/* Menu items */}
              <div className="py-1.5">
                <DropdownMenuItem asChild>
                  <Link
                    to="/dashboard"
                    data-testid="menu-dashboard-link"
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer focus:bg-zinc-800 focus:text-white"
                  >
                    <UserIcon className="w-3.5 h-3.5 text-zinc-500" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/support?tab=help"
                    data-testid="menu-support-link"
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer focus:bg-zinc-800 focus:text-white"
                  >
                    <LifeBuoy className="w-3.5 h-3.5 text-zinc-500" />
                    Help &amp; Support
                  </Link>
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/admin"
                      data-testid="menu-admin-link"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-emerald-300 hover:bg-zinc-800 cursor-pointer focus:bg-zinc-800"
                    >
                      <Shield className="w-3.5 h-3.5" />
                      Admin dashboard
                    </Link>
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/admin/spend"
                      data-testid="menu-admin-spend-link"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-emerald-300 hover:bg-zinc-800 cursor-pointer focus:bg-zinc-800"
                    >
                      <Coins className="w-3.5 h-3.5" />
                      Spend info
                    </Link>
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/admin/support"
                      data-testid="menu-admin-support-link"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-emerald-300 hover:bg-zinc-800 cursor-pointer focus:bg-zinc-800"
                    >
                      <LifeBuoy className="w-3.5 h-3.5" />
                      Support centre
                    </Link>
                  </DropdownMenuItem>
                )}
                {user?.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/admin/diagnostics"
                      data-testid="menu-admin-diagnostics-link"
                      className="flex items-center gap-2.5 px-4 py-2 text-sm text-emerald-300 hover:bg-zinc-800 cursor-pointer focus:bg-zinc-800"
                    >
                      <Activity className="w-3.5 h-3.5" />
                      Diagnostics
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  onSelect={handleLogout}
                  data-testid="menu-logout-btn"
                  className="flex items-center gap-2.5 px-4 py-2 text-sm text-zinc-300 hover:text-red-400 hover:bg-zinc-800 focus:bg-zinc-800 focus:text-red-400 cursor-pointer"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Sign out
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
